import { useAuthStore } from '../stores/auth';

/**
 * 권한 체크 composable.
 *
 * docs/05-rbac.md §4 (평가 알고리즘) + §5.2 (FE).
 *
 * 평가 순서:
 *   1. SUPER_ADMIN role 보유 시 항상 true (docs/05 §4 / §9)
 *   2. authStore.permissions 에 코드 포함 여부
 *
 * 코드 표기는 `{module}:{resource}:{action}` 콜론 표기 (docs/05 §2.1).
 *
 * `hasAnyPermission` 은 codes 중 하나 이상 보유,
 * `hasAllPermissions` 는 모든 codes 보유 — 백엔드 `@RequireAnyPermission` /
 * `@RequirePermission` 시맨틱과 1:1.
 */
const SUPER_ADMIN_ROLE_CODE = 'SUPER_ADMIN';

export interface PermissionApi {
  hasPermission: (code: string) => boolean;
  hasAnyPermission: (...codes: string[]) => boolean;
  hasAllPermissions: (...codes: string[]) => boolean;
}

export function usePermission(): PermissionApi {
  const authStore = useAuthStore();

  function isSuperAdmin(): boolean {
    return authStore.roles.includes(SUPER_ADMIN_ROLE_CODE);
  }

  function hasPermission(code: string): boolean {
    if (!code) return true;
    if (isSuperAdmin()) return true;
    return authStore.permissions.includes(code);
  }

  function hasAnyPermission(...codes: string[]): boolean {
    if (codes.length === 0) return true;
    if (isSuperAdmin()) return true;
    return codes.some((code) => authStore.permissions.includes(code));
  }

  function hasAllPermissions(...codes: string[]): boolean {
    if (codes.length === 0) return true;
    if (isSuperAdmin()) return true;
    return codes.every((code) => authStore.permissions.includes(code));
  }

  return { hasPermission, hasAnyPermission, hasAllPermissions };
}
